/**
 * How the fleet is drawn.
 *
 * Which stage a project is in, how each stage is doing, and what state every
 * milestone is in all arrive derived from the API — the five daily views draw the
 * same answer rather than each working it out. What is left is presentation: the
 * colour a stage reads at, the colour a calendar pill takes, the wording on a
 * lifecycle chip. Those live here so the card wall, the week board, the calendar,
 * the briefing and the wall display cannot describe one milestone in five ways.
 */

export const STAGE_STATE_LABELS = {
  complete: 'Done',
  escalation: 'Escalated',
  overdue: 'Overdue',
  'at-risk': 'At risk',
  active: 'In progress',
  upcoming: 'Upcoming',
  undated: 'No dates'
};

export const STAGE_STATE_COLOURS = {
  complete: 'success',
  escalation: 'error',
  overdue: 'error',
  'at-risk': 'warning',
  active: 'primary',
  upcoming: 'grey',
  undated: 'grey'
};

// The band a project's health reads at: trouble, not progress. `summarise` keeps
// the two apart, and so does this.
export const BAND_COLOURS = { red: 'error', yellow: 'warning', green: 'success' };

// A milestone's colour by the stage that owns it, so a pill on the calendar and
// the same milestone's segment on a lifecycle bar are the same colour.
export const STAGE_COLOURS = {
  readiness: '#6b6b6b',
  production: '#7c3aed',
  planogram: '#0891b2',
  quality: '#d97706',
  uat: '#16a34a',
  shipping: '#059669',
  delivery: '#2563eb',
  installation: '#db2777'
};

/**
 * A department's identity colour: the hue the team reads a department at,
 * carried over from the Ops Hub and resolved onto the stage colours above.
 * Where the source gave two departments one hue, the tie is broken by the
 * stage colour the department's milestones already wear on the calendar —
 * which is why vinyl reads amber here: a vinyl milestone is quality's on the
 * week board, and a schedule whose sidebar dot disagrees with its own rows'
 * calendar pills is two languages, not one.
 *
 * Colour is a cue, never the carrier — every place one of these is painted
 * keeps its label and icon, so the screen reads the same without it.
 */
export const DEPARTMENT_COLOURS = {
  procurement: STAGE_COLOURS.readiness,
  'mo-release': STAGE_COLOURS.readiness,
  inventory: '#0891b2',
  production: STAGE_COLOURS.production,
  software: '#2563eb',
  quality: STAGE_COLOURS.quality,
  qa: STAGE_COLOURS.quality,
  vinyl: STAGE_COLOURS.quality,
  planogram: STAGE_COLOURS.planogram,
  uat: STAGE_COLOURS.uat,
  shipping: STAGE_COLOURS.shipping,
  installation: STAGE_COLOURS.installation,
  deployment: STAGE_COLOURS.installation
};

export const stageStateLabel = (stage = {}) => STAGE_STATE_LABELS[stage.state] || stage.state || '';

export const stageStateColour = (stage = {}) => STAGE_STATE_COLOURS[stage.state] || 'grey';

export const bandColour = (health = {}) => BAND_COLOURS[health.band] || 'grey';

export const eventColour = (event = {}) => STAGE_COLOURS[event.stage] || STAGE_COLOURS.readiness;

export const departmentColour = (key) => DEPARTMENT_COLOURS[key] || null;

export const stageColour = (key) => STAGE_COLOURS[key] || STAGE_COLOURS.readiness;

// The days a week board shows. Weekend work is scheduled rarely enough that five
// columns is what a project manager reads, and an event that lands on a Saturday
// is reported in the overdue block rather than being hidden.
export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// A calendar day as `YYYY-MM-DD`, counted in UTC so a date never crosses a day
// boundary because of the reader's timezone.
export function isoDay(date) {
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  return utc.toISOString().slice(0, 10);
}

export function addDays(day, days) {
  const parsed = new Date(day + 'T00:00:00Z');
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

// The Monday of the week a day falls in.
export function mondayOf(day) {
  const parsed = new Date(day + 'T00:00:00Z');
  const weekday = parsed.getUTCDay();
  return addDays(day, weekday === 0 ? -6 : 1 - weekday);
}

export function formatDay(day) {
  if (!day) return '—';
  return new Date(day + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC'
  });
}

export function formatFullDay(day) {
  if (!day) return '—';
  return new Date(day + 'T00:00:00Z').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC'
  });
}

// How far out an event is, in the words a board uses.
export function whenLabel(event = {}) {
  const days = event.days_out;
  if (days === null || days === undefined) return '—';
  if (days === 0) return 'Today';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  return formatDay(event.date);
}
